import { useTranslation } from "react-i18next";
import { useCallback, useEffect, useRef, useState } from "react";
import { MultiSelect } from "primereact/multiselect";
import { Button } from "primereact/button";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Tag } from "primereact/tag";
import { Toast } from "primereact/toast";
import { SortOrder } from "primereact/api";
import findClient from "../../service/findClient";
import postVoucherGerar from "../../service/postVoucherGerar";
import findVoucher from "../../service/findVoucher";

export default function VoucherManager({ campanha, onClose, msgs }: { campanha: any, onClose: () => void, msgs: any }) {
  const { t } = useTranslation();
  const [clientes, setClientes] = useState<any[]>([]);
  const [selecionados, setSelecionados] = useState<string[]>([]);
  const [vouchers, setVouchers] = useState<any[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const toast = useRef<Toast>(null);
  const [lazyState, setlazyState] = useState({
    first: 0, rows: 10, page: 1, idCampanha: campanha?.id,
    sortField: 'id', sortOrder: SortOrder.DESC
  });

  useEffect(() => {
    // Apenas clientes ativos da franquia da campanha (matriz: todos quando a campanha não tem franquia).
    const lazyClient = {
      first: 0,
      rows: 99999999,
      page: 1,
      cliente: 'null',
      documento: 'null',
      idFranquia: campanha?.idFranquia ? campanha.idFranquia : null,
      inativo: 'false',
      sortField: 'nome',
      sortOrder: SortOrder.ASC
    };
    findClient(lazyClient).then((data) => setClientes(data.clientes ?? [])).catch(() => setClientes([]));
  }, []);

  const loadVouchers = useCallback(() => {
    findVoucher(lazyState).then((data) => { setTotalRecords(data.total); setVouchers(data.vouchers) })
      .catch(() => { setTotalRecords(0); setVouchers([]) });
  }, [lazyState]);

  useEffect(() => { loadVouchers(); }, [lazyState, loadVouchers]);

  const onPage = (event: any) => {
    setlazyState(prev => ({ ...prev, first: event.first, rows: event.rows, page: (event.first / event.rows) + 1 }));
  };

  const gerar = () => {
    if (!selecionados || selecionados.length === 0) {
      msgs.current?.clear();
      msgs.current?.show([{ sticky: false, life: 8000, severity: 'error', summary: 'Erro', detail: 'Selecione ao menos um cliente.', closable: false }]);
      return;
    }
    postVoucherGerar({ idCampanha: campanha.id, idsCliente: selecionados }).then((mensagem) => {
      toast.current?.show({ severity: 'success', summary: t('sucesso'), detail: mensagem, life: 4000 });
      setSelecionados([]);
      loadVouchers();
    }).catch((error) => toast.current?.show({ severity: 'error', summary: t('erro'), detail: error?.message || String(error), life: 5000 }));
  };

  const copiarHash = (hash: string) => {
    navigator.clipboard?.writeText(hash);
    toast.current?.show({ severity: 'info', summary: t('copiado'), detail: hash, life: 2000 });
  };

  const consumoBodyTemplate = (v: any) => <span>{v.quantidadeUtilizada} / {v.quantidade}</span>;

  const statusBodyTemplate = (v: any) => {
    if (v.finalizado) return <Tag icon="pi pi-flag" severity="danger" value={t('finalizado')} />;
    if (v.aplicado) return <Tag icon="pi pi-check-circle" severity="success" value={t('aplicado')} />;
    return <Tag icon="pi pi-clock" severity="warning" value={t('pendente')} />;
  };

  const hashBodyTemplate = (v: any) => (
    <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
      <span style={{ fontFamily: 'monospace' }}>{v.hash}</span>
      <Button type="button" icon="pi pi-copy" text onClick={() => copiarHash(v.hash)} tooltip={t('copiar') as string} aria-label="Copiar" />
    </span>
  );

  return (
    <div>
      <Toast ref={toast} />
      <div className="d-sm-flex align-items-center justify-content-between mb-4" style={{ display: 'flex', justifyContent: 'space-between' }}>
        <h2 className="h4 mb-0 text-gray"><i className="pi pi-ticket" style={{ marginRight: '0.5rem' }}></i> {t('vouchers')} — {campanha?.descricao}</h2>
        <Button icon="pi pi-arrow-left" label={t('voltar') as string} outlined onClick={onClose} type="button" />
      </div>
      <div className="grid" style={{ alignItems: 'end' }}>
        <div className="col-12 md:col-9 p-fluid">
          <label htmlFor="clientes" className="font-bold block mb-2">{t('clientes')}</label>
          <MultiSelect inputId="clientes" value={selecionados} onChange={(e) => setSelecionados(e.value)} options={clientes}
            optionLabel="nome" optionValue="id" filter display="chip" placeholder={t('selecionarClientes') as string} className="w-full" />
        </div>
        <div className="col-12 md:col-3 p-fluid">
          <Button label={t('gerarVouchers') as string} icon="pi pi-plus" style={{ backgroundColor: '#183462' }} onClick={gerar} type="button" />
        </div>
      </div>
      <br></br>
      <DataTable value={vouchers} first={lazyState.first} dataKey="id" paginator={true} lazy={true}
        emptyMessage="Nenhum voucher gerado"
        rows={lazyState.rows} totalRecords={totalRecords} onPage={onPage} tableStyle={{ minWidth: '60rem' }}>
        <Column field="clienteNome" header={t('cliente')} />
        <Column body={hashBodyTemplate} header="Voucher" />
        <Column body={consumoBodyTemplate} header={t('consumo')} />
        <Column body={statusBodyTemplate} header="Status" />
      </DataTable>
    </div>
  )
}
